import { notifications } from '@mantine/notifications';
import { useApi } from 'api/api-context';
import { AuthLoggedUserInfoResponse } from 'api/actions/auth-logged-user-info/auth-logged-user-info-response';
import { AuthTwoFactorStartSetupResponse } from 'api/actions/auth-two-factor-start-setup/auth-two-factor-start-setup-response';
import { FormMessage } from 'components/forms/FormProps';
import { ChangePasswordFormData } from 'components/forms/my-profile/ChangePasswordForm';
import MyProfileForm, { MyProfileFormData } from 'components/forms/my-profile/MyProfileForm';
import { FEATURE_TOGGLE_USER_NFQES_EMAIL } from 'env';
import panic from 'errors/panic';
import DashboardLayout from 'layouts/dashboard/DashboardLayout';
import { useCallback, useEffect, useState } from 'react';
import {
  DUPLICATE_USER_ERROR_CODE,
  ERROR_NOTIFICATION_COLOR,
  INVALID_PASSWORD_ERROR_CODE,
  PASSWORD_REUSE_ERROR_CODE,
  SUCCESS_NOTIFICATION_COLOR,
} from 'utils/constants';

/**
 * The personal profile page.
 *
 */
export default function MyProfilePage() {
  const { getAction, fullName } = useApi();
  const [passwordChangeMessage, setPasswordChangeMessage] = useState<FormMessage | undefined>(undefined);
  const [latestRecoveryCodes, setLatestRecoveryCodes] = useState<string[] | undefined>(undefined);
  const [data, setData] = useState<AuthLoggedUserInfoResponse | null>(null);

  const refreshProfile = useCallback(() => {
    const userGetMeAction = getAction('AuthLoggedUserInfo');

    return userGetMeAction().then(setData).catch(panic);
  }, [getAction]);

  useEffect(() => {
    void refreshProfile();
  }, [refreshProfile]);

  const clearLatestRecoveryCodes = useCallback(() => {
    setLatestRecoveryCodes(undefined);
  }, []);

  /**
   * Handles the form submission.
   */
  function onSubmit({
    email,
    nfqesEmail,
    firstName,
    lastName,
    phoneNumber,
    postNominals,
    preNominals,
    signatureFileId,
  }: MyProfileFormData) {
    const userUpdateMeAction = getAction('UserUpdateMe');

    userUpdateMeAction({
      payload: {
        preNominals,
        postNominals,
        firstName,
        lastName,
        email,
        ...(FEATURE_TOGGLE_USER_NFQES_EMAIL ? { nfqesEmail: nfqesEmail?.trim() || null } : {}),
        phoneNumber,
        signatureFileId: signatureFileId || undefined,
      },
    })
      .then(() => {
        notifications.show({
          title: 'Profil bol aktualizovaný',
          message: 'Vaše osobné informácie boli úspešne aktualizované.',
          color: SUCCESS_NOTIFICATION_COLOR,
        });
      })
      .catch((error) => {
        if (error.response.data.error.code === DUPLICATE_USER_ERROR_CODE) {
          notifications.show({
            title: 'Chyba!',
            message: 'Používateľ s danou emailovou adresou už existuje.',
            color: ERROR_NOTIFICATION_COLOR,
          });
        } else {
          panic(error);
        }
      });
  }

  /**
   * Handles the password change.
   */
  function changePassword({ password, newPassword, newPasswordRepeat }: ChangePasswordFormData) {
    const userChangePasswordAction = getAction('UserChangePassword');

    return userChangePasswordAction({ payload: { oldPassword: password, newPassword, newPasswordRepeat } })
      .then(() => {
        setPasswordChangeMessage({ type: 'success', message: 'Heslo bolo úspešne zmenené.' });
      })
      .catch((e) => {
        if (e?.response?.data?.error?.code === INVALID_PASSWORD_ERROR_CODE) {
          setPasswordChangeMessage({ type: 'fail', message: 'Zadali ste nesprávne heslo. Heslo nebolo zmenené.' });
        } else if (e?.response?.data?.error?.code === PASSWORD_REUSE_ERROR_CODE) {
          setPasswordChangeMessage({
            type: 'fail',
            message: 'Nové heslo sa musí líšiť od vášho aktuálneho hesla.',
          });
        } else {
          panic(e);
        }
      });
  }

  /**
   * Starts the 2FA setup flow and returns setup data for the authenticator app.
   */
  function startTwoFactorSetup(): Promise<AuthTwoFactorStartSetupResponse> {
    const action = getAction('AuthTwoFactorStartSetup');

    return action();
  }

  /**
   * Confirms 2FA setup with the current password and first generated TOTP code.
   */
  function confirmTwoFactorSetup(payload: { password: string; code: string }) {
    const action = getAction('AuthTwoFactorConfirmSetup');

    return action({ payload }).then((response) => {
      setLatestRecoveryCodes(response.recoveryCodes);

      return refreshProfile();
    });
  }

  /**
   * Disables 2FA after confirming the current password.
   */
  function disableTwoFactor(payload: { password: string }) {
    const action = getAction('AuthTwoFactorDisable');

    return action({ payload }).then(() => {
      setLatestRecoveryCodes(undefined);

      return refreshProfile();
    });
  }

  return (
    <DashboardLayout
      title="Editácia osobných údajov"
      breadcrumbs={[{ title: fullName || 'Profile' }, { title: 'Editácia osobných údajov' }]}
    >
      {!!data && (
        <MyProfileForm
          initialValues={{
            preNominals: data.preNominals ?? '',
            postNominals: data.postNominals ?? '',
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            nfqesEmail: data.nfqesEmail ?? '',
            phoneNumber: data.phoneNumber ?? '',
            signatureFileId: data.signatureFileId ?? '',
          }}
          passwordChangeMessage={passwordChangeMessage}
          latestRecoveryCodes={latestRecoveryCodes}
          twoFactorState={{
            twoFactorEnabled: data.twoFactorEnabled ?? false,
            twoFactorSetupPending: data.twoFactorSetupPending ?? false,
            twoFactorRequired: data.twoFactorRequired ?? false,
            twoFactorRecoveryCodesRemaining: data.twoFactorRecoveryCodesRemaining ?? 0,
          }}
          onSubmit={onSubmit}
          onPasswordChange={changePassword}
          onStartTwoFactorSetup={startTwoFactorSetup}
          onConfirmTwoFactorSetup={confirmTwoFactorSetup}
          onDisableTwoFactor={disableTwoFactor}
          onClearLatestRecoveryCodes={clearLatestRecoveryCodes}
        />
      )}
    </DashboardLayout>
  );
}
